import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  MediaDrop,
  createUploadSession,
  disposeWhenSettled,
  UploadStepError,
  type MediaDropProps,
  type UploadContext,
} from '@arthelper/ui';
import { failureProperties, stepOf } from '@arthelper/shared/upload-failure';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@artstorefronts/ui';
import { X } from 'lucide-react';
import { fileUtils } from '~/utils/file';
import { useImages } from '~/context/image';
import { useAnalytics } from '~/context/analytics';
import { ensureLeadingSlash } from '~/utils/collection';
import { useIsMobile } from '~/hooks/useIsMobile';
import { keepOpenForFilePicker } from '~/utils/file-picker-dialog';
import type { Collection } from '~/types';
import type { UploadSource } from '@arthelper/shared/upload-origin';
import { ArtProtectionBadge } from './ArtProtectionBadge';

interface UploaderProps {
  /** The surface mounting this uploader — stored on every image it creates (#4558). */
  source: UploadSource;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isFullPage?: boolean;
  showTitle?: boolean;
  isOnboarding?: boolean;
  targetCollectionId?: string | null;
  className?: string;
  isChat?: boolean;
  customHeaderText?: string;
  customSubtext?: string;
  preventAutoClose?: boolean;
}

type UploadResult = { imageId?: string; retryOf?: string };
type FolderFile = File & { path?: string; webkitRelativePath?: string };

function folderOf(file: FolderFile): string | null {
  const path = file.path || file.webkitRelativePath;
  if (!path || !path.includes('/')) return null;
  const parts = path.split('/').filter(Boolean);
  return parts.length > 1 ? ensureLeadingSlash(parts.slice(0, -1).join('/')) : null;
}

function Uploader({
  source: uploadSource,
  isOpen,
  onOpenChange,
  isFullPage = false,
  showTitle = true,
  isOnboarding = false,
  targetCollectionId,
  isChat = false,
  customHeaderText,
  customSubtext,
  preventAutoClose = false,
}: UploaderProps) {
  const { t } = useTranslation('artwork');
  const { t: tCommon } = useTranslation('common');
  const { images, uploadImage, createCollection, failedUploads, wasDismissed } = useImages();
  const { amplitude } = useAnalytics();
  const isMobile = useIsMobile();
  const [session] = useState(() => createUploadSession());
  const disposalTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set when this drop closes the dialog, cleared on the next microtask. onRejected runs
  // synchronously after onFilesAdded, so it can see that the in-drop notice will be unseen.
  const rejectionUnseen = useRef(false);
  const folderPromises = useRef(new Map<string, Promise<Collection>>());
  const hasProcessedImages = images.some((image) => image.status === 'processed');

  useEffect(() => {
    if (disposalTimer.current) clearTimeout(disposalTimer.current);
    // Drain, don't abort: My Artwork swaps the full-page uploader out the moment an
    // upload starts (and hides it once images land), so the batch must outlive it.
    return () => {
      disposalTimer.current = setTimeout(() => disposeWhenSettled(session), 0);
    };
  }, [session]);

  const resolveCollection = useCallback(
    async (file: File): Promise<string | null> => {
      if (targetCollectionId != null) return targetCollectionId;
      const folder = folderOf(file as FolderFile);
      if (!folder) return null;
      // Each level is created under its parent's server-returned path, so the server's
      // `Name (1)` rename of a taken folder carries down to everything nested in it.
      // shortcut: folders are cached per mounted Uploader, not per drop — dropping the same folder
      // twice in one session merges into one collection; key by drop if users hit it.
      const ensure = (path: string): Promise<Collection> => {
        const cached = folderPromises.current.get(path);
        if (cached) return cached;
        const cut = path.lastIndexOf('/');
        const parent: Promise<Collection | null> = cut > 0 ? ensure(path.slice(0, cut)) : Promise.resolve(null);
        const pending = parent
          .then((created) =>
            createCollection({ path: `${created?.path ?? ''}${path.slice(cut)}`, name: path.slice(cut + 1) })
          )
          .catch((error) => {
            folderPromises.current.delete(path);
            throw error;
          });
        folderPromises.current.set(path, pending);
        return pending;
      };
      return String((await ensure(folder)).id);
    },
    [targetCollectionId, createCollection]
  );

  const uploader = useCallback(
    async (source: File, ctx: UploadContext): Promise<UploadResult> => {
      const previous = (ctx.previousResult as UploadResult | undefined)?.retryOf;
      // Dismissed from My Artwork: nothing is left to retry, so the chip sends its file as a new upload.
      const retryOf = previous && wasDismissed(previous) ? undefined : previous;
      // Gone from the failed list = already retried from My Artwork; sending it again would duplicate it.
      if (retryOf && !failedUploads.some((item) => item.id === retryOf)) return {};

      let file = source;
      if (/\.tiff?$/i.test(source.name) || ['image/tiff', 'image/x-tiff'].includes(source.type)) {
        const converted = await fileUtils.convertTiffToJpeg(source).catch((error: unknown) => {
          console.error('Error converting TIFF:', error);
          return null;
        });
        if (!converted) {
          amplitude.track('image_upload_failed', failureProperties('pick', source, 'TIFF conversion failed'));
          toast.error(t('image-manager.upload-failures.tiff-failed', { name: source.name }), {
            id: `upload-tiff-${source.name}`,
            description: t('image-manager.upload-failures.tiff-failed-description'),
          });
          throw new Error('TIFF conversion failed');
        }
        file = converted;
        ctx.setPreview?.(converted);
      }
      if (ctx.signal.aborted) throw new DOMException('Upload cancelled', 'AbortError');
      const collectionId = await resolveCollection(source);
      if (ctx.signal.aborted) throw new DOMException('Upload cancelled', 'AbortError');
      try {
        const created = await uploadImage({ file, collectionId, source: uploadSource, retryOf, uploadContext: ctx });
        return { imageId: String(created.id) };
      } catch (error) {
        if (ctx.signal.aborted) throw error;
        const failedId = (error as Error & { uploadClientId?: string }).uploadClientId;
        if (!failedId) throw error;
        throw new UploadStepError(error instanceof Error ? error.message : String(error), stepOf(error), {
          retryOf: failedId,
        });
      }
    },
    [failedUploads, wasDismissed, resolveCollection, uploadImage, uploadSource, amplitude, t]
  );

  const labels = useMemo<MediaDropProps['labels']>(
    () => ({
      added: (count) => t('media-drop.added', { count }),
      dropMore: t('media-drop.drop-more'),
    }),
    [t]
  );

  const drop = (
    <MediaDrop
      session={session}
      width="100%"
      fill={!isFullPage}
      tone="neutral"
      accept={['image/jpeg', 'image/png', 'image/webp', 'image/tiff', '.tif', '.tiff']}
      maxSizeMB={{ image: 100 }}
      maxFiles={1000}
      concurrency={3}
      uploader={uploader}
      labels={labels}
      prompt={`${t('image-manager.uploader.dropzone-text')} ${t('image-manager.uploader.dropzone-browse')}`}
      hint={t('image-manager.uploader.formats-hint-desktop')}
      onFilesAdded={() => {
        if (!preventAutoClose && !isFullPage) {
          rejectionUnseen.current = true;
          onOpenChange(false);
          queueMicrotask(() => {
            rejectionUnseen.current = false;
          });
        }
      }}
      onRejected={(rejections) => {
        // Accepted files close the dialog, so MediaDrop's own notice goes unseen and the
        // toast is the only record. A dialog that stays open already shows that notice;
        // a toast would sit on the page outside the frame. Full-page upload has no dialog.
        const noticeUnseen = rejectionUnseen.current || isFullPage;
        rejectionUnseen.current = false;
        for (const rejection of rejections) {
          amplitude.track('image_upload_failed', {
            ...failureProperties('pick', rejection.file, rejection.message),
            rejection: rejection.reason === 'custom' ? 'other' : rejection.reason,
          });
          if (noticeUnseen) {
            toast.error(t('image-manager.upload-failures.rejected', { name: rejection.file.name }), {
              id: `upload-rejected-${rejection.file.name}`,
              description: t(
                `image-manager.upload-failures.${rejection.reason === 'type' ? 'rejected-type' : 'rejected-other'}`
              ),
            });
          }
        }
      }}
    />
  );

  if (images.length > 0 && isFullPage && !isChat) return null;

  if (isFullPage) {
    return (
      <div className={isOnboarding ? 'max-w-7xl w-full mx-auto pt-12 pb-12' : 'h-full w-full mx-auto'}>
        {showTitle && (
          <h1 className="text-2xl font-medium tracking-tight text-zinc-900 mb-8 text-center">
            {t('image-manager.uploader.full-page-title')}
          </h1>
        )}
        {drop}
        {!hasProcessedImages && __COPYRIGHT_SCANNING_ENABLED__ && <ArtProtectionBadge compact={isChat} />}
      </div>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="z-[60] flex h-[80vh] w-full max-w-[calc(100%-2rem)] flex-col sm:max-w-[1200px]"
        onOpenAutoFocus={(event: Event) => event.preventDefault()}
        onPointerDownOutside={keepOpenForFilePicker}
        showCloseButton={false}
      >
        <DialogHeader className="flex-row items-start gap-3 text-left">
          <div className="min-w-0 flex-1">
            <DialogTitle className="break-words leading-snug">
              {customHeaderText ?? t('image-manager.uploader.dialog-title')}
            </DialogTitle>
            <DialogDescription className="mx-0 max-w-none">
              {customSubtext ??
                (isMobile
                  ? t('image-manager.uploader.dialog-description-mobile')
                  : t('image-manager.uploader.dialog-description-desktop'))}
            </DialogDescription>
          </div>
          <DialogClose asChild>
            <Button
              aria-label={tCommon('close')}
              className="size-8 shrink-0"
              size="icon-sm"
              type="button"
              variant="ghost"
            >
              <X aria-hidden="true" />
            </Button>
          </DialogClose>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-auto px-6">
          {drop}
          {!hasProcessedImages && __COPYRIGHT_SCANNING_ENABLED__ && <ArtProtectionBadge compact={isChat} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default Uploader;
